/**
 * Resource tag detail — display / edit / create modes
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Moon,
  Pencil,
  Plus,
  Sun,
  Trash2,
} from 'lucide-react'

import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  TagValueBadge,
  ResourceTagRecordsTable,
  RecordFormDialog,
  DeleteResourceTagDialog,
  DeleteRecordsDialog,
} from '@/components/resource-tags'
import {
  useResourceTag,
  useCreateResourceTag,
  useUpdateResourceTag,
  useDeleteResourceTag,
  useAddResourceTagRecord,
  useUpdateResourceTagRecord,
  useDeleteResourceTagRecord,
  useBulkDeleteResourceTagRecords,
} from '@/hooks/use-resource-tags'
import { useThemeStore } from '@/stores/theme-store'
import type {
  ResourceTagRecord,
  ResourceTagRecordInput,
} from '@/types/resource-tag'

type LocalRecord = ResourceTagRecordInput & { localId: string }

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export function ResourceTagDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useThemeStore()

  const isCreate = id === 'new'
  const isEdit = !isCreate && searchParams.get('mode') === 'edit'
  const isDisplay = !isCreate && !isEdit

  const { data: tag, isLoading, isError, error } = useResourceTag(isCreate ? undefined : id)

  const createMutation = useCreateResourceTag()
  const updateMutation = useUpdateResourceTag(id ?? '')
  const deleteTagMutation = useDeleteResourceTag()
  const addRecordMutation = useAddResourceTagRecord(id ?? '')
  const updateRecordMutation = useUpdateResourceTagRecord(id ?? '')
  const deleteRecordMutation = useDeleteResourceTagRecord(id ?? '')
  const bulkDeleteMutation = useBulkDeleteResourceTagRecords(id ?? '')

  // Header draft (create + edit)
  const [resourceTagName, setResourceTagName] = useState('')
  const [tagValue, setTagValue] = useState('')
  const [description, setDescription] = useState('')

  // Create-mode local records
  const [localRecords, setLocalRecords] = useState<LocalRecord[]>([])

  // Selection + dialogs
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [recordDialogOpen, setRecordDialogOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ResourceTagRecord | null>(null)
  const [editingLocalId, setEditingLocalId] = useState<string | null>(null)
  const [deleteTagOpen, setDeleteTagOpen] = useState(false)
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([])

  useEffect(() => {
    if (tag && !isCreate) {
      setResourceTagName(tag.resourceTag)
      setTagValue(tag.tagValue)
      setDescription(tag.description ?? '')
    }
  }, [tag, isCreate])

  useEffect(() => {
    setSelectedIds(new Set())
  }, [id, isEdit])

  const records: ResourceTagRecord[] = useMemo(() => {
    if (isCreate) {
      return localRecords.map((r) => ({
        id: r.localId,
        resourceAttribute: r.resourceAttribute,
        resourceValue: r.resourceValue,
        status: 'ACTIVE',
        createdAt: '',
        updatedAt: '',
      }))
    }
    return tag?.records ?? []
  }, [isCreate, localRecords, tag])

  const canEditRecords = isCreate || isEdit
  const headerEditable = isCreate || isEdit
  const nameEditable = isCreate

  const enterEdit = useCallback(() => {
    setSearchParams({ mode: 'edit' })
  }, [setSearchParams])

  const leaveEdit = useCallback(() => {
    if (tag) {
      setResourceTagName(tag.resourceTag)
      setTagValue(tag.tagValue)
      setDescription(tag.description ?? '')
    }
    setSearchParams({})
  }, [tag, setSearchParams])

  const handleSaveHeader = useCallback(async () => {
    if (!id || isCreate) return
    if (!tagValue.trim()) return
    try {
      await updateMutation.mutateAsync({
        tagValue: tagValue.trim(),
        description: description.trim() || null,
      })
      setSearchParams({})
    } catch {
      // toast in hook
    }
  }, [id, isCreate, tagValue, description, updateMutation, setSearchParams])

  const handleCreate = useCallback(async () => {
    if (!resourceTagName.trim() || !tagValue.trim()) return
    try {
      const created = await createMutation.mutateAsync({
        resourceTag: resourceTagName.trim(),
        tagValue: tagValue.trim(),
        description: description.trim() || null,
        records: localRecords.map(({ resourceAttribute, resourceValue }) => ({
          resourceAttribute,
          resourceValue,
        })),
      })
      navigate(`/pac/resource-tags/${created.id}`, { replace: true })
    } catch {
      // toast in hook
    }
  }, [
    resourceTagName,
    tagValue,
    description,
    localRecords,
    createMutation,
    navigate,
  ])

  const openAddRecord = () => {
    setEditingRecord(null)
    setEditingLocalId(null)
    setRecordDialogOpen(true)
  }

  const openEditRecord = (record: ResourceTagRecord) => {
    if (isCreate) {
      setEditingLocalId(record.id)
      setEditingRecord(null)
    } else {
      setEditingRecord(record)
      setEditingLocalId(null)
    }
    setRecordDialogOpen(true)
  }

  const handleRecordSubmit = async (data: ResourceTagRecordInput) => {
    if (isCreate) {
      if (editingLocalId) {
        setLocalRecords((prev) =>
          prev.map((r) =>
            r.localId === editingLocalId
              ? { ...r, resourceValue: data.resourceValue }
              : r
          )
        )
      } else {
        setLocalRecords((prev) => [
          ...prev,
          { ...data, localId: crypto.randomUUID() },
        ])
      }
      setRecordDialogOpen(false)
      return
    }

    try {
      if (editingRecord) {
        await updateRecordMutation.mutateAsync({
          recordId: editingRecord.id,
          data: { resourceValue: data.resourceValue },
        })
      } else {
        await addRecordMutation.mutateAsync(data)
      }
      setRecordDialogOpen(false)
    } catch {
      // toast in hook
    }
  }

  const handleDeleteRecords = async () => {
    if (pendingDeleteIds.length === 0) return

    if (isCreate) {
      setLocalRecords((prev) => prev.filter((r) => !pendingDeleteIds.includes(r.localId)))
      setSelectedIds(new Set())
      setPendingDeleteIds([])
      return
    }

    try {
      if (pendingDeleteIds.length === 1) {
        await deleteRecordMutation.mutateAsync(pendingDeleteIds[0])
      } else {
        await bulkDeleteMutation.mutateAsync(pendingDeleteIds)
      }
      setSelectedIds(new Set())
      setPendingDeleteIds([])
    } catch {
      // toast in hook
    }
  }

  const handleDeleteTag = async () => {
    if (!id || isCreate) return
    try {
      await deleteTagMutation.mutateAsync(id)
      navigate('/pac/resource-tags')
    } catch {
      // toast in hook
    }
  }

  const recordDialogInitial = editingRecord
    ? {
        resourceAttribute: editingRecord.resourceAttribute,
        resourceValue: editingRecord.resourceValue,
      }
    : editingLocalId
      ? (() => {
          const r = localRecords.find((x) => x.localId === editingLocalId)
          return r
            ? { resourceAttribute: r.resourceAttribute, resourceValue: r.resourceValue }
            : null
        })()
      : null

  const recordBusy =
    addRecordMutation.isPending ||
    updateRecordMutation.isPending ||
    createMutation.isPending

  if (!isCreate && isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!isCreate && (isError || !tag)) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Data classification not found'}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/pac/resource-tags')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Button>
      </div>
    )
  }

  const displayName = isCreate ? resourceTagName || 'New Classification' : tag!.resourceTag
  const displayValue = isCreate ? tagValue : isEdit ? tagValue : tag!.tagValue

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex min-w-0 items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate('/pac/resource-tags')}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="truncate text-base font-medium">
            {displayName || 'New Classification'}
          </h1>
          {displayValue ? <TagValueBadge value={displayValue} /> : null}
          {isCreate && (
            <Badge variant="outline" className="shrink-0">
              New
            </Badge>
          )}
          {isEdit && (
            <Badge variant="outline" className="shrink-0">
              Editing
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {isDisplay && (
            <>
              <Button variant="outline" size="sm" className="h-8" onClick={enterEdit}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-8"
                onClick={() => setDeleteTagOpen(true)}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-6 p-4 md:p-6">
        {/* Header fields */}
        <section className="grid max-w-2xl gap-4">
          <div className="grid gap-2">
            <Label htmlFor="tag-name">
              Data Classification Attribute{' '}
              {isCreate && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="tag-name"
              value={isCreate || isEdit ? resourceTagName : tag!.resourceTag}
              onChange={(e) => setResourceTagName(e.target.value)}
              disabled={!nameEditable}
              placeholder="e.g. Material-ITARControlled"
              autoComplete="off"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tag-value">
              Value {(isCreate || isEdit) && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="tag-value"
              value={isCreate || isEdit ? tagValue : tag!.tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              disabled={!headerEditable}
              placeholder="e.g. TRUE"
              autoComplete="off"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tag-description">Description</Label>
            <Textarea
              id="tag-description"
              value={isCreate || isEdit ? description : tag!.description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!headerEditable}
              rows={3}
              placeholder="Optional description"
            />
          </div>

          {!isCreate && tag && (
            <p className="text-xs text-muted-foreground">
              Updated {formatDate(tag.updatedAt)}
              {tag.createdAt ? ` · Created ${formatDate(tag.createdAt)}` : ''}
            </p>
          )}
        </section>

        <Separator />

        {/* Records */}
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">Records</h2>
              <Badge variant="secondary">{records.length}</Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canEditRecords || (isCreate && !resourceTagName.trim())}
                onClick={openAddRecord}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEditRecords || selectedIds.size === 0}
                onClick={() => setPendingDeleteIds([...selectedIds])}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </Button>
            </div>
          </div>

          <ResourceTagRecordsTable
            records={records}
            editable={canEditRecords}
            selectedIds={selectedIds}
            onToggleSelect={(rid) => {
              setSelectedIds((prev) => {
                const next = new Set(prev)
                if (next.has(rid)) next.delete(rid)
                else next.add(rid)
                return next
              })
            }}
            onToggleSelectAll={(checked) => {
              setSelectedIds(checked ? new Set(records.map((r) => r.id)) : new Set())
            }}
            onEdit={openEditRecord}
            onDelete={(record) => setPendingDeleteIds([record.id])}
          />
        </section>

        {/* Footer actions */}
        {(isCreate || isEdit) && (
          <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background py-4">
            <Button
              variant="outline"
              onClick={() =>
                isCreate ? navigate('/pac/resource-tags') : leaveEdit()
              }
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Cancel
            </Button>
            {isCreate ? (
              <Button
                onClick={handleCreate}
                disabled={
                  !resourceTagName.trim() ||
                  !tagValue.trim() ||
                  createMutation.isPending
                }
              >
                {createMutation.isPending ? 'Creating…' : 'Create classification'}
              </Button>
            ) : (
              <Button
                onClick={handleSaveHeader}
                disabled={!tagValue.trim() || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        )}
      </div>

      <RecordFormDialog
        open={recordDialogOpen}
        onOpenChange={setRecordDialogOpen}
        mode={editingRecord || editingLocalId ? 'edit' : 'add'}
        resourceTagName={
          isCreate
            ? resourceTagName.trim() || 'New Classification'
            : tag?.resourceTag ?? ''
        }
        initial={recordDialogInitial}
        onSubmit={handleRecordSubmit}
        isLoading={recordBusy}
      />

      <DeleteResourceTagDialog
        open={deleteTagOpen}
        onOpenChange={setDeleteTagOpen}
        resourceTag={tag?.resourceTag ?? ''}
        tagValue={tag?.tagValue ?? ''}
        onConfirm={handleDeleteTag}
        isLoading={deleteTagMutation.isPending}
      />

      <DeleteRecordsDialog
        open={pendingDeleteIds.length > 0}
        onOpenChange={(open) => !open && setPendingDeleteIds([])}
        count={pendingDeleteIds.length}
        onConfirm={handleDeleteRecords}
        isLoading={deleteRecordMutation.isPending || bulkDeleteMutation.isPending}
      />
    </>
  )
}
